import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { X, Building } from 'lucide-react'
import { Button } from '../common'
import { useQuery } from 'react-query'
import { companyService } from '../../services'
import { useAuth } from '../../contexts/AuthContextTest'

function BankDetailForm({ 
  isOpen, 
  editingBankDetail, 
  onClose, 
  onSubmit, 
  isLoading 
}) {
  const { user } = useAuth()
  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  // Fetch companies for dropdown (only if user is Super Admin)
  const { data: companiesData } = useQuery(
    'companies',
    () => companyService.getAll({ limit: 100 }),
    { enabled: user?.role?.id === 1 }
  )
  const companies = companiesData?.data?.companies || companiesData?.companies || []

  // Reset form when editing bank detail changes
  useEffect(() => {
    if (editingBankDetail) {
      reset({
        id: editingBankDetail.id,
        companyId: editingBankDetail.companyId || user?.company?.id || '',
        bankName: editingBankDetail.bankName || '',
        accountNumber: editingBankDetail.accountNumber || '',
        ifscCode: editingBankDetail.ifscCode || '',
        branchName: editingBankDetail.branchName || '',
        isActive: editingBankDetail.isActive !== undefined ? editingBankDetail.isActive : true
      })
    } else {
      reset({
        companyId: user?.company?.id || '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        branchName: '',
        isActive: true
      })
    }
  }, [editingBankDetail, reset, user])

  const handleSubmitForm = (data) => {
    console.log('BankDetailForm - Submitting data:', data)
    console.log('BankDetailForm - Editing bank detail:', editingBankDetail)
    console.log('BankDetailForm - Has ID:', !!data.id)
    onSubmit(data)
  }

  const handleClose = () => {
    reset({
      companyId: user?.company?.id || '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      branchName: '',
      isActive: true
    })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={handleClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Building className="h-6 w-6 text-primary-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-secondary-900">
                    {editingBankDetail ? 'Edit Bank Detail' : 'Add New Bank Detail'}
                  </h3>
                  <p className="text-base text-secondary-500">
                    {editingBankDetail ? 'Update bank detail information' : 'Enter bank detail information'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-6">
              <input type="hidden" {...register('id')} />
              
              <div className="modal-body">
                {/* Company Selection (only for Super Admin) */}
                {user?.role?.id === 1 && (
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Company *
                    </label>
                    <select
                      {...register('companyId', { required: 'Company is required' })}
                      className="input"
                    >
                      <option value="">Select Company</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    {errors.companyId && (
                      <p className="text-danger-600 text-base mt-1">{errors.companyId.message}</p>
                    )}
                  </div>
                )}

                {/* Bank Information */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2">
                    Bank Information
                  </h4>
                  
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Bank Name *
                    </label>
                    <input
                      {...register('bankName', { required: 'Bank name is required' })}
                      className="input"
                      placeholder="Enter bank name"
                    />
                    {errors.bankName && (
                      <p className="text-danger-600 text-base mt-1">{errors.bankName.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Account Number *
                      </label>
                      <input
                        {...register('accountNumber', { required: 'Account number is required' })}
                        className="input"
                        placeholder="Enter account number"
                      />
                      {errors.accountNumber && (
                        <p className="text-danger-600 text-base mt-1">{errors.accountNumber.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        IFSC Code *
                      </label>
                      <input
                        {...register('ifscCode', { 
                          required: 'IFSC code is required',
                          pattern: {
                            value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                            message: 'Invalid IFSC code format (e.g., ABCD0123456)'
                          }
                        })}
                        className="input"
                        placeholder="Enter IFSC code"
                        style={{ textTransform: 'uppercase' }}
                      />
                      {errors.ifscCode && (
                        <p className="text-danger-600 text-base mt-1">{errors.ifscCode.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Branch Name *
                    </label>
                    <input
                      {...register('branchName', { required: 'Branch name is required' })}
                      className="input"
                      placeholder="Enter branch name"
                    />
                    {errors.branchName && (
                      <p className="text-danger-600 text-base mt-1">{errors.branchName.message}</p>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2">
                    Status
                  </h4>
                  
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Status
                    </label>
                    <select {...register('isActive')} className="input">
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : editingBankDetail ? 'Update Bank Detail' : 'Create Bank Detail'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BankDetailForm

